import { useEffect, useId, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import { Chart, LinearScale, LineElement, PointElement, ScatterController, Tooltip } from "chart.js";
import type { ChartDataset, ChartOptions, Plugin } from "chart.js";

import { Box, useTheme } from "@mui/material";

import {
  ARROW_HEAD_ANGLE,
  ARROW_HEAD_LENGTH,
  ARROW_LINE_WIDTH,
} from "../../../../../../components/common/chart-plugins/orbit-connecting-lines";
import { BODE_FREQUENCY_SCALE, type TBodeFrequencyScale } from "../../../multi-analysis/types";
import { getNyquistTooltipLines, getNyquistTooltipPosition, NYQUIST_TOOLTIP_COLORS } from "../nyquist/tooltip-utils";
import {
  convertBodeFrequency,
  getBodeArrowSegments,
  getBodeCursorOrder,
  getBodeFrequencyAxisBounds,
  getBodeSegmentArrow,
  type IBodeDirectionArrow,
} from "./chart-utils";
import { BODE_PHASE_UNIT_CODE, formatBodeUnitLabel, type IBodeSample, type IBodeSeries } from "./model";

Chart.register(ScatterController, LinearScale, LineElement, PointElement, Tooltip);

type BodeChartKind = "amplitude" | "phase";

interface IBodeChartPoint {
  x: number;
  y: number;
  key: string;
  seriesKey: string;
  sample: IBodeSample;
}

export interface IBodeChartProps {
  kind: BodeChartKind;
  series: readonly IBodeSeries[];
  cursorKey: string | null;
  onCursorChange: (key: string) => void;
  formatValue: (value: number, unitCode: number | null) => string;
  frequencyScale: TBodeFrequencyScale;
  frequencyAxisFromZero: boolean;
  markerMode: boolean;
  hiddenSeriesKeys?: readonly string[];
}

type BodeChartInstance = Chart<"scatter", IBodeChartPoint[]>;
type BodeDataset = ChartDataset<"scatter", IBodeChartPoint[]>;

const drawDirectionArrow = (
  context: CanvasRenderingContext2D,
  arrow: IBodeDirectionArrow,
  color: string,
  area: { left: number; top: number; right: number; bottom: number },
) => {
  if (arrow.x < area.left || arrow.x > area.right || arrow.y < area.top || arrow.y > area.bottom) return;
  context.save();
  context.beginPath();
  context.rect(area.left, area.top, area.right - area.left, area.bottom - area.top);
  context.clip();
  context.strokeStyle = color;
  context.lineWidth = ARROW_LINE_WIDTH;
  context.lineCap = "round";
  context.beginPath();
  context.moveTo(arrow.x, arrow.y);
  context.lineTo(
    arrow.x - ARROW_HEAD_LENGTH * Math.cos(arrow.angle - ARROW_HEAD_ANGLE),
    arrow.y - ARROW_HEAD_LENGTH * Math.sin(arrow.angle - ARROW_HEAD_ANGLE),
  );
  context.moveTo(arrow.x, arrow.y);
  context.lineTo(
    arrow.x - ARROW_HEAD_LENGTH * Math.cos(arrow.angle + ARROW_HEAD_ANGLE),
    arrow.y - ARROW_HEAD_LENGTH * Math.sin(arrow.angle + ARROW_HEAD_ANGLE),
  );
  context.stroke();
  context.restore();
};

export const BodeChart = ({
  kind,
  series,
  cursorKey,
  onCursorChange,
  formatValue,
  frequencyScale,
  frequencyAxisFromZero,
  markerMode,
  hiddenSeriesKeys,
}: IBodeChartProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const hintId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipHoveredRef = useRef(false);
  const tooltipFocusedRef = useRef(false);
  const chartRef = useRef<BodeChartInstance | null>(null);
  const cursorRef = useRef(cursorKey);
  const onCursorChangeRef = useRef(onCursorChange);
  const hiddenSeriesRef = useRef(hiddenSeriesKeys);
  cursorRef.current = cursorKey;
  onCursorChangeRef.current = onCursorChange;
  hiddenSeriesRef.current = hiddenSeriesKeys;
  const orderedSeries = useMemo(
    () =>
      series.map(item => ({
        ...item,
        samples: item.samples.slice().sort((left, right) => left.date.localeCompare(right.date)),
      })),
    [series],
  );
  const chartLabel =
    kind === "amplitude"
      ? formatMessage({ id: "pages.speed.bode.amplitude" })
      : formatMessage({ id: "pages.speed.bode.phase" });
  const frequencyTitle = formatMessage({
    id:
      frequencyScale === BODE_FREQUENCY_SCALE.HERTZ
        ? "pages.speed.bode.frequencyHertzUnit"
        : "pages.scheme.analytics.rotationFrequencyRpmUnit",
  });
  const keyboardHint = formatMessage({ id: "pages.speed.bode.chartKeyboardHint" });
  const phaseUnits = formatBodeUnitLabel(formatMessage({ id: `enums.units.${BODE_PHASE_UNIT_CODE}` }));
  const visibleSeries = useMemo(
    () => orderedSeries.filter(item => !hiddenSeriesKeys?.includes(item.key)),
    [hiddenSeriesKeys, orderedSeries],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const textColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const font = { size: 12, family: theme.typography.fontFamily };
    const frequenciesRpm = orderedSeries
      .flatMap(item => item.samples.map(sample => sample.rotationFrequencyRpm))
      .filter((value): value is number => value !== null && Number.isFinite(value));
    const frequencyAxisBounds = getBodeFrequencyAxisBounds(frequenciesRpm, frequencyScale, frequencyAxisFromZero);
    const datasets: BodeDataset[] = orderedSeries.map(item => ({
      label: item.name,
      data: item.samples.map(sample => ({
        x:
          sample.rotationFrequencyRpm === null
            ? Number.NaN
            : convertBodeFrequency(sample.rotationFrequencyRpm, frequencyScale),
        y: kind === "amplitude" ? sample.amplitude : sample.unwrappedPhaseDegrees ?? Number.NaN,
        key: sample.key,
        seriesKey: item.key,
        sample,
      })),
      borderColor: item.color,
      backgroundColor: item.color,
      borderWidth: 1.5,
      pointRadius: 2.5,
      pointHoverRadius: 5,
      pointHitRadius: 9,
      showLine: true,
      spanGaps: false,
      tension: 0,
      hidden: hiddenSeriesRef.current?.includes(item.key) ?? false,
    }));
    const pointIndexes = datasets.map(dataset => new Map(dataset.data.map((point, index) => [point.key, index])));
    const arrows = datasets.map(dataset => getBodeArrowSegments(dataset.data));
    const sourceByKey = new Map(orderedSeries.map(item => [item.key, item]));
    const tooltipLabels = {
      point: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" }),
      measure: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" }),
      dateTime: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" }),
      amplitude: formatMessage({ id: "pages.speed.bode.amplitude" }),
      phase: formatMessage({ id: "pages.speed.bode.phase" }),
    };
    const tooltipLines = (point: IBodeChartPoint) => {
      const source = sourceByKey.get(point.seriesKey);
      if (!source) return [];
      const phase = point.sample.unwrappedPhaseDegrees ?? point.sample.phaseDegrees;
      return getNyquistTooltipLines(
        {
          pointName: source.pointName,
          measureName: source.measureName,
          date: point.sample.date,
          amplitude: point.sample.amplitude,
          phaseDegrees: phase !== null && Number.isFinite(phase) ? phase : null,
          unitCode: source.unitCode,
          amplitudeUnits: source.units,
          phaseUnits,
        },
        tooltipLabels,
        formatValue,
      );
    };
    const decoration: Plugin<"scatter"> = {
      id: `bode-${kind}-decoration`,
      afterDatasetsDraw: chart => {
        const context = chart.ctx;
        context.save();
        const selected = markerMode
          ? datasets.flatMap((dataset, datasetIndex) => {
              if (!chart.isDatasetVisible(datasetIndex)) return [];
              const index = cursorRef.current === null ? undefined : pointIndexes[datasetIndex].get(cursorRef.current);
              const point = index === undefined ? undefined : chart.getDatasetMeta(datasetIndex).data[index];
              return point && Number.isFinite(dataset.data[index!]?.x) && Number.isFinite(dataset.data[index!]?.y)
                ? [point]
                : [];
            })[0]
          : undefined;
        if (selected) {
          context.beginPath();
          context.setLineDash([3, 4]);
          context.strokeStyle = textColor;
          context.lineWidth = 1;
          context.moveTo(selected.x, chart.chartArea.top);
          context.lineTo(selected.x, chart.chartArea.bottom);
          context.stroke();
          context.setLineDash([]);
        }
        datasets.forEach((dataset, datasetIndex) => {
          if (!chart.isDatasetVisible(datasetIndex)) return;
          const rendered = chart.getDatasetMeta(datasetIndex).data;
          const pointIndex =
            !markerMode || cursorRef.current === null ? undefined : pointIndexes[datasetIndex].get(cursorRef.current);
          const cursorPoint = pointIndex === undefined ? undefined : rendered[pointIndex];
          if (
            cursorPoint &&
            Number.isFinite(dataset.data[pointIndex!]?.x) &&
            Number.isFinite(dataset.data[pointIndex!]?.y)
          ) {
            context.beginPath();
            context.arc(cursorPoint.x, cursorPoint.y, 6, 0, Math.PI * 2);
            context.fillStyle = theme.palette.background.paper;
            context.fill();
            context.strokeStyle = String(dataset.borderColor);
            context.lineWidth = 2;
            context.stroke();
          }
          arrows[datasetIndex].forEach(segment => {
            const from = rendered[segment.from];
            const to = rendered[segment.to];
            const arrow = from && to ? getBodeSegmentArrow(from, to) : null;
            if (arrow) drawDirectionArrow(context, arrow, String(dataset.borderColor), chart.chartArea);
          });
        });
        context.restore();
      },
    };
    const disabledPlugins = {
      crosshair: false,
      selectionZoom: false,
      alarmLevel: false,
      htmlLegend: false,
      drawConnectingLines: false,
      drawOrbitConnectingLines: false,
      harmonicCursors: false,
      subHarmonicCursors: false,
      additionalCursorLabels: false,
      faultFrequenciesCursors: false,
      markerPassageLines: false,
    } as const;
    const options: ChartOptions<"scatter"> = {
      animation: false,
      responsive: false,
      maintainAspectRatio: false,
      interaction: { mode: "nearest", intersect: false },
      onClick: (_event, elements) => {
        const selected = elements[0];
        const point = selected ? datasets[selected.datasetIndex]?.data[selected.index] : null;
        if (point) onCursorChangeRef.current(point.key);
      },
      scales: {
        x: {
          type: "linear",
          min: frequencyAxisBounds.min,
          max: frequencyAxisBounds.max,
          display: kind === "phase",
          title: { display: kind === "phase", text: frequencyTitle, color: textColor, font },
          grid: { color: gridColor },
          ticks: { color: textColor, font, maxTicksLimit: 8, maxRotation: 0 },
        },
        y: {
          type: "linear",
          position: "left",
          beginAtZero: kind === "amplitude",
          grace: "10%",
          title: {
            display: true,
            text:
              kind === "amplitude"
                ? `${chartLabel}, ${orderedSeries[0]?.units ?? ""}`
                : `${chartLabel}, ${formatMessage({ id: `enums.units.${BODE_PHASE_UNIT_CODE}` })}`,
            color: textColor,
            font,
          },
          ticks: {
            color: textColor,
            font,
            maxTicksLimit: 6,
            callback: value =>
              formatValue(
                Number(value),
                kind === "amplitude" ? orderedSeries[0]?.unitCode ?? null : BODE_PHASE_UNIT_CODE,
              ),
          },
          grid: { color: gridColor },
          afterFit: axis => {
            axis.width = 82;
          },
        },
      },
      plugins: {
        ...disabledPlugins,
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: ({ tooltip }) => {
            const element = tooltipRef.current;
            if (!element) return;
            if (!tooltip.opacity && !tooltipHoveredRef.current && !tooltipFocusedRef.current) {
              element.style.display = "none";
              return;
            }
            if (!tooltip.opacity) return;
            const points = [
              ...new Map(
                tooltip.dataPoints.map(item => {
                  const point = datasets[item.datasetIndex]?.data[item.dataIndex];
                  return [point?.key, point] as const;
                }),
              ).values(),
            ].filter((point): point is IBodeChartPoint => Boolean(point));
            const anchor = tooltip.dataPoints[0]?.element;
            if (!anchor || !points.length) {
              element.style.display = "none";
              return;
            }
            element.replaceChildren(
              ...points.flatMap((point, index) =>
                tooltipLines(point).map((line, row) => {
                  const text = document.createElement("div");
                  text.textContent = line;
                  text.style.fontWeight = row === 0 ? "600" : "400";
                  text.style.color = row === 0 ? NYQUIST_TOOLTIP_COLORS.title : NYQUIST_TOOLTIP_COLORS.body;
                  if (index > 0 && row === 0) text.style.marginTop = "8px";
                  return text;
                }),
              ),
            );
            element.style.display = "block";
            const position = getNyquistTooltipPosition(
              anchor,
              { width: element.offsetWidth, height: element.offsetHeight },
              { width: container.clientWidth, height: container.clientHeight },
            );
            element.style.left = `${position.x}px`;
            element.style.top = `${position.y}px`;
          },
          callbacks: {
            title: items => {
              const point = items[0] ? datasets[items[0].datasetIndex]?.data[items[0].dataIndex] : null;
              return point ? tooltipLines(point)[2] ?? "" : "";
            },
            label: item => {
              const point = datasets[item.datasetIndex]?.data[item.dataIndex];
              return point ? tooltipLines(point) : [];
            },
          },
        },
      },
    };
    const chart = new Chart<"scatter", IBodeChartPoint[]>(canvas, {
      type: "scatter",
      data: { datasets },
      options,
      plugins: [decoration],
    });
    chartRef.current = chart;
    let resizeFrame = 0;
    const resize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        if (container.clientWidth > 0 && container.clientHeight > 0)
          chart.resize(container.clientWidth, container.clientHeight);
      });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    return () => {
      cancelAnimationFrame(resizeFrame);
      observer.disconnect();
      chartRef.current = null;
      tooltipHoveredRef.current = false;
      tooltipFocusedRef.current = false;
      if (tooltipRef.current) tooltipRef.current.style.display = "none";
      chart.destroy();
    };
  }, [
    chartLabel,
    formatMessage,
    formatValue,
    frequencyAxisFromZero,
    frequencyScale,
    frequencyTitle,
    kind,
    markerMode,
    orderedSeries,
    phaseUnits,
    theme.palette,
    theme.typography.fontFamily,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const hidden = new Set(hiddenSeriesKeys ?? []);
    chart.data.datasets.forEach((dataset, index) =>
      chart.setDatasetVisibility(index, !hidden.has(dataset.data[0]?.seriesKey ?? "")),
    );
    if (kind === "amplitude") {
      const visible = orderedSeries.find(item => !hidden.has(item.key));
      const scale = chart.options.scales?.y;
      if (visible && scale) {
        scale.title = {
          display: true,
          text: `${chartLabel}, ${visible.units}`,
          color: scale.title?.color,
          font: scale.title?.font,
        };
        scale.ticks = {
          ...scale.ticks,
          callback: value => formatValue(Number(value), visible.unitCode),
        };
      }
    }
    chart.update("none");
  }, [chartLabel, formatValue, hiddenSeriesKeys, kind, orderedSeries]);

  useEffect(() => {
    chartRef.current?.draw();
  }, [cursorKey]);

  const cursorOrder = useMemo(() => getBodeCursorOrder(visibleSeries, cursorKey), [cursorKey, visibleSeries]);

  return (
    <Box
      ref={containerRef}
      data-bode-chart={kind}
      data-bode-cursor-key={cursorKey ?? ""}
      onMouseOutCapture={event => {
        const next = event.relatedTarget;
        tooltipHoveredRef.current = next instanceof Node && Boolean(tooltipRef.current?.contains(next));
      }}
      sx={{ flex: 1, minWidth: 0, minHeight: 0, position: "relative", overflow: "hidden" }}
    >
      <canvas
        ref={canvasRef}
        role="img"
        tabIndex={0}
        aria-label={chartLabel}
        aria-describedby={hintId}
        onKeyDown={event => {
          if (event.altKey || event.ctrlKey || event.metaKey || !["ArrowLeft", "ArrowRight"].includes(event.key))
            return;
          const current = cursorOrder.findIndex(sample => sample.key === cursorKey);
          const nextIndex =
            event.key === "ArrowLeft"
              ? Math.max(0, current < 0 ? cursorOrder.length - 1 : current - 1)
              : Math.min(cursorOrder.length - 1, current + 1);
          const next = cursorOrder[nextIndex];
          if (next) {
            event.preventDefault();
            onCursorChange(next.key);
          }
        }}
        style={{ display: "block", width: "100%", height: "100%", cursor: markerMode ? "crosshair" : undefined }}
      >
        {chartLabel}
      </canvas>
      <Box
        ref={tooltipRef}
        role="tooltip"
        tabIndex={0}
        data-bode-tooltip={kind}
        onMouseEnter={() => {
          tooltipHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          tooltipHoveredRef.current = false;
          if (!tooltipFocusedRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onFocus={() => {
          tooltipFocusedRef.current = true;
        }}
        onBlur={() => {
          tooltipFocusedRef.current = false;
          if (!tooltipHoveredRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onKeyDown={event => {
          if (event.key === "Escape") {
            event.stopPropagation();
            tooltipFocusedRef.current = false;
            tooltipHoveredRef.current = false;
            if (tooltipRef.current) tooltipRef.current.style.display = "none";
            canvasRef.current?.focus();
          }
        }}
        sx={{
          display: "none",
          position: "absolute",
          pointerEvents: "auto",
          zIndex: 3,
          maxWidth: "min(360px, calc(100% - 8px))",
          maxHeight: "calc(100% - 8px)",
          overflowY: "auto",
          overscrollBehavior: "contain",
          boxSizing: "border-box",
          p: 1,
          borderRadius: 1,
          bgcolor: NYQUIST_TOOLTIP_COLORS.background,
          color: NYQUIST_TOOLTIP_COLORS.body,
          fontSize: 12,
          fontFamily: theme.typography.fontFamily,
          lineHeight: 1.5,
          overflowWrap: "anywhere",
          boxShadow: 3,
        }}
      />
      <Box
        id={hintId}
        component="span"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clipPath: "inset(50%)",
        }}
      >
        {keyboardHint}
      </Box>
    </Box>
  );
};
